import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Layout, LoadingScreen, ErrorNotice, StatusPill } from '../components/Layout';
import { api, appointmentImageUrl } from '../lib/api';
import type { Appointment } from '../lib/types';

export function AppointmentDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appointment = useQuery({
    queryKey: ['appointment', id],
    queryFn: () => api<{ data: Appointment }>(`/api/appointments/${id}`).then((r) => r.data),
  });
  const removeEvent = useMutation({
    mutationFn: () => api(`/api/appointments/${id}/calendar-event`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['appointment', id] }),
  });
  const remove = useMutation({
    mutationFn: async () => {
      if (appointment.data?.googleEventId) await api(`/api/appointments/${id}/calendar-event`, { method: 'DELETE' });
      return api(`/api/appointments/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => navigate('/appointments'),
  });
  const retryMeetingSync = useMutation({
    mutationFn: () => api(`/api/appointments/${id}/meeting-sync`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['appointment', id] }),
  });
  if (appointment.isLoading) return <Layout title="รายละเอียดใบนัด"><LoadingScreen /></Layout>;
  const item = appointment.data;
  if (!item) return <Layout title="รายละเอียดใบนัด"><ErrorNotice message="ไม่พบใบนัดนี้" /></Layout>;
  const templateText = (key: string) => typeof item.templateData[key] === 'string' ? item.templateData[key] : null;
  const templateList = (key: string) => Array.isArray(item.templateData[key]) ? (item.templateData[key] as string[]).join('\n') : null;
  const details = item.templateType === 'meeting' ? [
    ['วันประชุม', `${item.appointmentDate ?? '-'} ${item.startTime ?? ''}–${item.endTime ?? ''}`],
    ['หน่วยงาน/ผู้จัด', item.organization], ['ผู้ประสานงาน', item.doctorName], ['ผู้เข้าร่วม', templateList('participants')],
    ['สถานที่/ลิงก์', item.location], ['วาระประชุม', item.instructions], ['เอกสารที่ต้องเตรียม', templateList('materials')],
    ['งานที่เกี่ยวข้อง', templateText('relatedTaskTitle')], ['กำหนดส่งงาน', templateText('relatedTaskDueDate')], ['เลขอ้างอิง', item.referenceNumber],
  ] : item.templateType === 'task' ? [
    ['วันครบกำหนด', `${item.appointmentDate ?? '-'} ${item.startTime ?? ''}${item.endTime ? `–${item.endTime}` : ''}`],
    ['หน่วยงาน', item.organization], ['ผู้มอบหมาย/ผู้ติดต่อ', item.doctorName], ['สถานที่', item.location],
    ['ระดับความสำคัญ', templateText('priority')], ['วันที่ประชุมที่เกี่ยวข้อง', templateText('relatedMeetingDate')],
    ['สรุปประเด็นสำคัญ', item.instructions], ['เลขอ้างอิง', item.referenceNumber], ['ข้อมูลอื่น', item.otherNotes],
  ] : [
    ['วันที่', `${item.appointmentDate ?? '-'} ${item.startTime ?? ''}–${item.endTime ?? ''}`],
    ['สถานพยาบาล', item.organization], ['แผนก', item.department], ['แพทย์/ผู้ติดต่อ', item.doctorName],
    ['ประเภทบริการ/หัตถการ', templateText('serviceType')], ['สถานที่', item.location], ['เลขอ้างอิง', item.referenceNumber],
    ['โทรศัพท์', item.phone], ['ชื่อผู้รับบริการ', item.patientName], ['สิ่งที่ต้องเตรียม', templateList('preparationItems')],
    ['ข้อแนะนำ', item.instructions], ['ข้อมูลอื่น', item.otherNotes],
  ];
  return (
    <Layout title="รายละเอียดใบนัด">
      <section className="mx-auto max-w-2xl space-y-4">
        {item.hasImage && <img src={appointmentImageUrl(id)} alt="ภาพใบนัด" className="max-h-96 w-full rounded-2xl bg-slate-100 object-contain" />}
        <div className="card">
          <div className="flex items-start justify-between gap-3"><h2 className="text-xl font-bold">{item.title ?? 'ใบนัด'}</h2><StatusPill status={item.status} /></div>
          <dl className="mt-4 divide-y divide-slate-100">
            {details.filter(([, value]) => value).map(([label, value]) => (
              <div key={label} className="grid grid-cols-[8rem_1fr] gap-3 py-2.5 text-sm"><dt className="text-slate-500">{label}</dt><dd className="whitespace-pre-wrap font-medium">{value}</dd></div>
            ))}
          </dl>
        </div>
        {item.ocr?.rawText && <details className="card"><summary className="cursor-pointer font-semibold">ข้อความ OCR ฉบับเต็ม</summary><pre className="mt-3 whitespace-pre-wrap text-sm text-slate-600">{item.ocr.rawText}</pre></details>}
        <div className="grid gap-3 sm:grid-cols-2">
          <Link className="btn-secondary" to={`/appointments/${id}/review`}>แก้ไขข้อมูล</Link>
          {!item.googleEventId && <Link className="btn-primary" to={`/appointments/${id}/calendar`}>เพิ่มใน Calendar</Link>}
          {item.googleEventUrl && <a className="btn-primary" href={item.googleEventUrl} target="_blank" rel="noreferrer">เปิด Google Calendar</a>}
          {item.googleEventId && <button className="btn-secondary text-red-700" onClick={() => removeEvent.mutate()} disabled={removeEvent.isPending}>ลบกิจกรรม Calendar</button>}
        </div>
        {item.templateType === 'meeting' && item.googleEventId && (
          <div className={`rounded-2xl border p-4 ${item.meetingSync.status === 'synced' ? 'border-teal-200 bg-teal-50' : 'border-amber-300 bg-amber-50'}`}>
            <p className="font-bold">
              {item.meetingSync.status === 'synced' ? 'เพิ่มในเรื่องประชุมแล้ว' : 'รอสร้างรายการประชุม'}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link className="btn-secondary" to="/meetings">เปิดเรื่องประชุม</Link>
              {item.meetingSync.status !== 'synced' && (
                <button className="btn-secondary" onClick={() => retryMeetingSync.mutate()} disabled={retryMeetingSync.isPending}>
                  {retryMeetingSync.isPending ? 'กำลังลองใหม่…' : 'ลองอีกครั้ง'}
                </button>
              )}
            </div>
          </div>
        )}
        {(remove.error || removeEvent.error) && <ErrorNotice message={(remove.error ?? removeEvent.error)?.message ?? 'ดำเนินการไม่สำเร็จ'} />}
        <button className="mt-5 min-h-12 w-full rounded-xl border border-red-200 text-red-700" onClick={() => { if (confirm('ลบรายการ ไฟล์เอกสาร และกิจกรรม Calendar (ถ้ามี)?')) remove.mutate(); }} disabled={remove.isPending}>ลบรายการและข้อมูลที่เกี่ยวข้อง</button>
      </section>
    </Layout>
  );
}
